'use client';

import { useEffect, useState } from 'react';
import { useQuality } from '@/components/shared/QualityProvider';
import { ATMOSPHERE_STARS } from '@/lib/motionTokens';

type Star = {
  x: number;       // % across viewport
  y: number;       // % down viewport
  size: number;    // px (kept tiny — ≤ 2px)
  opacity: number;
  delay: number;   // animation delay
  duration: number; // twinkle period
};

// Per-star delay + duration ranges live in `lib/motionTokens.ts` →
// ATMOSPHERE_STARS. Size and opacity stay inline because they're
// visual-language values, not motion timing.
function generateStars(): Star[] {
  const { count, delayMinSeconds, delayMaxSeconds, durationMinSeconds, durationMaxSeconds } = ATMOSPHERE_STARS;
  const delaySpan = delayMaxSeconds - delayMinSeconds;
  const durationSpan = durationMaxSeconds - durationMinSeconds;
  return Array.from({ length: count }, () => ({
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: 0.8 + Math.random() * 1.2,
    opacity: 0.25 + Math.random() * 0.35,
    delay: delayMinSeconds + Math.random() * delaySpan,
    duration: durationMinSeconds + Math.random() * durationSpan,
  }));
}

/**
 * Sparse pinprick starfield + featherweight vignette behind the page.
 * Position: fixed inset: 0 (see globals.css `.intro-atmosphere`), so it
 * persists across the journey and the info section — the void universe
 * is the same in both. No scroll-based opacity fade; the corridor scene
 * renders on top during the journey, the blob renders on top during the
 * info section, and these stars + vignette provide a consistent backdrop.
 *
 * Stars generate in useEffect — module-level Math.random would cause a
 * hydration mismatch between SSR and client. Skipped on low tier and
 * prefers-reduced-motion.
 */
export function IntroAtmosphere() {
  const { tier } = useQuality();
  const [stars, setStars] = useState<Star[] | null>(null);

  const enabled = tier === 'high' || tier === 'mid' || tier === 'low';

  useEffect(() => {
    if (!enabled) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    // Client-only generation by design: Math.random would hydration-mismatch
    // if called during render. setStars in the effect IS the pattern here;
    // no cascade, runs once per `enabled` flip.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStars(generateStars());
  }, [enabled]);

  if (!enabled || !stars) return null;

  return (
    <div aria-hidden className="intro-atmosphere">
      <div className="intro-atmosphere__vignette" />
      <div className="intro-atmosphere__stars">
        {stars.map((s, i) => (
          <span
            key={i}
            className="intro-atmosphere__star"
            style={{
              left: `${s.x}%`,
              top: `${s.y}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              opacity: s.opacity,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
